import type { AnySelfOwnedWidgetClass, GetInitialData, SelfOwnedWidget, WidgetId } from '../selfOwnedWidget';
import type { Container, ContainerInitArgs, ContainerJSON, ContainerLifecycle, WidgetHostContainer } from './Container';
import { ContainerModel } from './ContainerModel';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { TypedContainerLike } from '@thinkalpha/common/ioc/TypedContainer.js';
import type { Layout, WidgetTab, WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import { inject, injectable } from 'src/features/ioc';
import { removeContainer, setMaximizedContainer } from 'src/store/actions/container';
import { addTab, removeTab as removeTabAction, updateActiveTab, updateTabName } from 'src/store/actions/tab';
import { onTabChannelChange, resetChannelData } from 'src/store/actions/widgetAndChannel';
import type { IsomorphicBindings } from 'src/types/bindings';
import { v4 as uuidv4 } from 'uuid';

const STILL_UNINITIALIZED = (action: string) =>
    new Error(`Container.${action} called while uninitialized; init() must be called first`);
const ALREADY_DISPOSED = (action: string) => new Error(`Container.${action} called on a disposed Container`);

interface SelfOwnedWidgetEntry {
    widget: SelfOwnedWidget<unknown>;
    childIoc: TypedContainerLike<Record<never, never>>;
}

interface PerWidgetChildBindings {
    WidgetId: WidgetId;
    WorkspaceContainer: Container;
    WidgetHostContainer: WidgetHostContainer;
    GetInitialData: GetInitialData<unknown>;
}

@injectable()
export class ContainerImpl implements Container, AsyncDisposable {
    readonly #lifecycle = signal<ContainerLifecycle>({ kind: 'uninitialized' });
    #model: ContainerModel | undefined;

    readonly #selfOwnedWidgetEntries = new Map<WidgetId, SelfOwnedWidgetEntry>();

    // Empty defaults shared across pre-init access. Returning the same instance
    // each time keeps signal identity stable for consumers that subscribe before
    // `init` has fired (e.g., async IPC paths).
    readonly #emptyTabs = signal<readonly WidgetTab[]>([]);
    readonly #emptyActiveTabId = signal<string | null>(null);
    readonly #emptyIsMaximized = signal<boolean>(false);
    readonly #emptyDirty = signal<boolean>(false);
    readonly #emptyLayout = signal<Layout>({ x: 0, y: 0, w: 0, h: 0 });

    constructor(
        @inject('Store') private readonly store: IsomorphicBindings['Store'],
        @inject('IoCContainer') private readonly iocContainer: IsomorphicBindings['IoCContainer'],
        @inject('SelfOwnedWidgetRegistry')
        private readonly selfOwnedWidgetRegistry: IsomorphicBindings['SelfOwnedWidgetRegistry'],
        @inject('ContainerTabResolver')
        private readonly tabResolver: IsomorphicBindings['ContainerTabResolver'],
    ) {}

    get lifecycle(): ReadonlySignal<ContainerLifecycle> {
        return this.#lifecycle;
    }

    get position(): ReadonlySignal<Layout> {
        return this.#model?.position ?? this.#emptyLayout;
    }

    get tabs(): ReadonlySignal<readonly WidgetTab[]> {
        return this.#model?.tabs ?? this.#emptyTabs;
    }

    get activeTabId(): ReadonlySignal<string | null> {
        return this.#model?.activeTabId ?? this.#emptyActiveTabId;
    }

    get isMaximized(): ReadonlySignal<boolean> {
        return this.#model?.isMaximized ?? this.#emptyIsMaximized;
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model?.dirty ?? this.#emptyDirty;
    }

    async init(args: ContainerInitArgs): Promise<void> {
        const state = this.#lifecycle.value;
        if (state.kind !== 'uninitialized') {
            // init is non-idempotent: a second call indicates a programming error.
            throw new Error(`Container.init called from lifecycle state '${state.kind}' (must be uninitialized)`);
        }

        this.#model = new ContainerModel({
            id: args.id,
            windowId: args.windowId,
            layout: args.layout,
            store: this.store,
        });

        this.#lifecycle.value = { kind: 'initialized', id: args.id, windowId: args.windowId };

        // Hydration currently flows through WorkspaceService's ledger and
        // Redux fan-out; keep `initialJSON` available for the native path.
        void args.initialJSON;
    }

    async dispose(): Promise<void> {
        const state = this.#lifecycle.value;
        if (state.kind === 'disposed' || state.kind === 'disposing') return;

        this.#lifecycle.value = { kind: 'disposing' };
        this.#disposeAllWidgets();
        this.#model?.[Symbol.dispose]();
        this.#model = undefined;
        this.#lifecycle.value = { kind: 'disposed' };
    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.dispose();
    }

    async addInitialWidget(type: WidgetType, tabId: WidgetId): Promise<void> {
        this.#requireInitialized('addInitialWidget');
        const widgetClass = this.selfOwnedWidgetRegistry.get(type);
        if (!widgetClass) {
            return;
        }
        await this.#registerSelfOwnedWidget(widgetClass, tabId);
    }

    async addTab(type: WidgetType, _opts?: { x?: number; y?: number }): Promise<WidgetId> {
        this.#requireInitialized('addTab');
        const tabId = uuidv4() as WidgetId;
        const widgetClass = this.selfOwnedWidgetRegistry.get(type);

        if (widgetClass) {
            await this.#registerSelfOwnedWidget(widgetClass, tabId);
        }

        const containerId = this.#assertedContainerId('addTab');
        const widget = widgetClass
            ? ({ type, channelId: null } as unknown as WidgetTab['widget'])
            : ({ type } as unknown as WidgetTab['widget']);
        this.store.dispatch(
            addTab(
                {
                    id: tabId,
                    name: undefined,
                    widget,
                    containerId,
                    channelId: null,
                    isActive: true,
                } as never,
                containerId,
            ),
        );

        return tabId;
    }

    async removeWidget(tabId: WidgetId): Promise<void> {
        this.#requireInitialized('removeWidget');
        if (APP_ELECTRON) {
            await this.iocContainer.get('WorkspaceService').removeWidget(tabId);
            return;
        }
        await this.removeTab(tabId);
    }

    async disposeWidget(tabId: WidgetId): Promise<void> {
        this.#requireInitialized('disposeWidget');
        const entry = this.#selfOwnedWidgetEntries.get(tabId);
        if (entry) {
            this.#disposeWidget(tabId, entry);
        }
    }

    getWidget<T extends SelfOwnedWidget<unknown>>(tabId: WidgetId): T | undefined {
        return this.#selfOwnedWidgetEntries.get(tabId)?.widget as T | undefined;
    }

    getWidgetIoc(tabId: WidgetId): TypedContainerLike<Record<never, never>> | undefined {
        return this.#selfOwnedWidgetEntries.get(tabId)?.childIoc;
    }

    setTabChannel(tabId: WidgetId, channel: string | null): void {
        this.#requireInitialized('setTabChannel');
        this.store.dispatch(onTabChannelChange(tabId, channel));
    }

    resetTabChannelData(tabId: WidgetId): void {
        this.#requireInitialized('resetTabChannelData');
        this.store.dispatch(resetChannelData(tabId));
    }

    setTabName(tabId: WidgetId, name: string): void {
        this.#requireInitialized('setTabName');
        this.store.dispatch(updateTabName(tabId, name));
    }

    setActiveTab(tabId: WidgetId): void {
        this.#requireInitialized('setActiveTab');
        const containerId = this.#assertedContainerId('setActiveTab');
        this.store.dispatch(updateActiveTab(containerId, tabId));
    }

    async removeTab(tabId: WidgetId): Promise<void> {
        this.#requireInitialized('removeTab');
        if (APP_ELECTRON) {
            await this.iocContainer.get('WorkspaceService').removeWidget(tabId);
            return;
        }
        const entry = this.#selfOwnedWidgetEntries.get(tabId);
        if (entry) {
            this.#disposeWidget(tabId, entry);
        }
        const containerId = this.#assertedContainerId('removeTab');
        const state = this.store.getState();
        const containerVm = state.container.byId[containerId];
        if (!containerVm) return;

        if (containerVm.tabIds.length <= 1) {
            this.store.dispatch(removeContainer(containerId));
            return;
        }
        const newActiveTabId = containerVm.tabIds.find((id) => id !== tabId);
        const widgetType = state.tab.byId[tabId]?.widget?.type ?? 'unknown';
        this.store.dispatch(removeTabAction(tabId, containerId, newActiveTabId!, widgetType));
    }

    toggleMaximized(): void {
        this.#requireInitialized('toggleMaximized');
        const containerId = this.#assertedContainerId('toggleMaximized');
        const next = this.isMaximized.value ? undefined : containerId;
        this.store.dispatch(setMaximizedContainer(next));
    }

    async deleteSelf(): Promise<void> {
        this.#requireInitialized('deleteSelf');
        const containerId = this.#assertedContainerId('deleteSelf');
        if (APP_ELECTRON) {
            await this.iocContainer.get('WorkspaceService').removeContainer(containerId);
            return;
        }
        this.store.dispatch(removeContainer(containerId));
    }

    async toJSON(): Promise<ContainerJSON> {
        this.#requireInitialized('toJSON');
        const json = await this.#model!.toJSON();
        return {
            ...json,
            tabs: json.tabs.map((tab) => {
                const tabId = tab.id as WidgetId;
                const widget = this.#selfOwnedWidgetEntries.get(tabId)?.widget;
                if (!widget) return tab;

                const widgetClass = this.selfOwnedWidgetRegistry.get(tab.widget.type);
                if (!widgetClass) return tab;

                try {
                    const persistedState = widgetClass.stateSchema.parse(widget.toJSON()) as Record<string, unknown>;
                    return {
                        ...tab,
                        widget: { ...tab.widget, ...persistedState } as WidgetTab['widget'],
                    };
                } catch {
                    return tab;
                }
            }),
        };
    }

    async markCleanAll(): Promise<void> {
        this.#requireInitialized('markCleanAll');
        await this.#model!.markCleanAll();
    }

    #requireInitialized(action: string): void {
        const state = this.#lifecycle.value;
        if (state.kind === 'uninitialized') throw STILL_UNINITIALIZED(action);
        if (state.kind === 'disposed' || state.kind === 'disposing') throw ALREADY_DISPOSED(action);
    }

    #assertedContainerId(action: string): string {
        const state = this.#lifecycle.value;
        if (state.kind !== 'initialized') throw STILL_UNINITIALIZED(action);
        return state.id;
    }

    async #registerSelfOwnedWidget(widgetClass: AnySelfOwnedWidgetClass, tabId: WidgetId): Promise<void> {
        const child = (
            this.iocContainer as TypedContainerLike<PerWidgetChildBindings>
        ).createChild<PerWidgetChildBindings>();
        child.bind('WidgetId').toConstantValue(tabId);
        child.bind('WorkspaceContainer').toConstantValue(this);
        child.bind('WidgetHostContainer').toConstantValue(this);
        child.bind('GetInitialData').toConstantValue(() => this.tabResolver.getInitialData(tabId));

        const widget = await widgetClass.fromJSON(child as never);

        const childAsBase = child as unknown as TypedContainerLike<Record<never, never>>;
        const entry: SelfOwnedWidgetEntry = { widget, childIoc: childAsBase };
        this.#selfOwnedWidgetEntries.set(tabId, entry);
        this.#publishWidgetMap();
    }

    #disposeWidget(tabId: WidgetId, entry: SelfOwnedWidgetEntry): void {
        this.#selfOwnedWidgetEntries.delete(tabId);
        const widget = entry.widget as SelfOwnedWidget<unknown> & Partial<Disposable>;
        widget[Symbol.dispose]?.();
        void entry.childIoc;
        this.#publishWidgetMap();
    }

    #disposeAllWidgets(): void {
        for (const [tabId, entry] of this.#selfOwnedWidgetEntries) {
            this.#disposeWidget(tabId, entry);
        }
    }

    #publishWidgetMap(): void {
        if (!this.#model) return;
        const next = new Map<WidgetId, SelfOwnedWidget<unknown>>();
        for (const [id, entry] of this.#selfOwnedWidgetEntries) {
            next.set(id, entry.widget);
        }
        this.#model.setSelfOwnedWidgets(next);
    }
}
